import { useLiveQuery } from 'dexie-react-hooks'
import { motion } from 'framer-motion'
import { MapPin, Phone, Repeat, ScrollText, StickyNote, Tag } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { StatusBadge } from '@/components/StatusBadge'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import type { ClientTip, Produs } from '@/db/dexie'
import { getClient, getComenziByClient, getProduse } from '@/db/queries'
import { fmtData, fmtKg, fmtRON, fmtSuma } from '@/lib/format'
import { cn } from '@/lib/utils'

type BadgeVariant = 'warning' | 'danger' | 'success' | 'secondary' | 'muted'

/** Shared client-type metadata (label + warm-editorial badge variant). */
export const TIP_META: Record<ClientTip, { label: string; variant: BadgeVariant }> = {
  carmangerie: { label: 'Carmangerie', variant: 'danger' },
  magazin: { label: 'Magazin', variant: 'muted' },
  minimarket: { label: 'Minimarket', variant: 'secondary' },
  restaurant: { label: 'Restaurant', variant: 'warning' },
  supermarket: { label: 'Supermarket', variant: 'success' },
}

export function TipBadge({ tip, className }: { tip: ClientTip; className?: string }) {
  const m = TIP_META[tip]
  return (
    <Badge variant={m.variant} className={className}>
      {m.label}
    </Badge>
  )
}

function Sectiune({
  icon: Icon,
  titlu,
  extra,
  children,
}: {
  icon: typeof Repeat
  titlu: string
  extra?: string
  children: React.ReactNode
}) {
  return (
    <section className="px-6 py-5">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="size-4 text-amber-ink" />
        <h3 className="font-display text-sm font-medium">{titlu}</h3>
        {extra && <span className="text-xs text-muted-foreground">· {extra}</span>}
      </div>
      {children}
    </section>
  )
}

export function ClientDetail({
  clientId,
  onClose,
}: {
  clientId: string | null
  onClose: () => void
}) {
  // keep the last-selected id so content stays populated through the close animation
  const [shownId, setShownId] = useState<string | null>(null)
  useEffect(() => {
    if (clientId) setShownId(clientId)
  }, [clientId])

  const client = useLiveQuery(() => (shownId ? getClient(shownId) : undefined), [shownId])
  const comenzi = useLiveQuery(() => (shownId ? getComenziByClient(shownId) : []), [shownId]) ?? []
  const produse = useLiveQuery(() => getProduse(), []) ?? []

  const produseById = useMemo(() => new Map(produse.map((p) => [p.id, p])), [produse])

  const uzuala = useMemo(() => {
    if (!client) return [] as { produs?: Produs; cantitateKg: number; produsId: string }[]
    return client.comandaUzuala.map((u) => ({
      produsId: u.produsId,
      cantitateKg: u.cantitateKg,
      produs: produseById.get(u.produsId),
    }))
  }, [client, produseById])

  const preturiSpeciale = useMemo(() => {
    if (!client)
      return [] as { produs?: Produs; standard: number; special: number; produsId: string }[]
    return Object.entries(client.pretSpecial).map(([produsId, special]) => {
      const produs = produseById.get(produsId)
      return { produsId, produs, special, standard: produs?.pretKg ?? special }
    })
  }, [client, produseById])

  return (
    <Sheet
      open={clientId != null}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-lg">
        {client && (
          <>
            <div className="border-b border-border px-6 pt-6 pb-5">
              <div className="flex items-start gap-3 pr-8">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-amber-soft font-display text-lg font-medium text-amber-ink">
                  {client.nume.charAt(0)}
                </div>
                <div className="min-w-0">
                  <h2 className="font-display text-xl font-medium leading-tight">{client.nume}</h2>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <TipBadge tip={client.tip} />
                    <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="size-3.5" />
                      {client.localitate}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-1.5 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="size-3.5 shrink-0" />
                  <span className="font-mono nums text-foreground">{client.telefon}</span>
                </div>
                <div className="flex items-start gap-2 text-muted-foreground">
                  <MapPin className="size-3.5 shrink-0 translate-y-0.5" />
                  <span className="text-foreground">{client.adresa}</span>
                </div>
              </div>

              <div
                className={cn(
                  'mt-4 flex items-center justify-between rounded-lg border px-3.5 py-2.5',
                  client.soldRestant > 0
                    ? 'border-tomato/25 bg-tomato-soft'
                    : 'border-border bg-muted/40',
                )}
              >
                <span
                  className={cn(
                    'text-xs font-medium uppercase tracking-wide',
                    client.soldRestant > 0 ? 'text-tomato' : 'text-muted-foreground',
                  )}
                >
                  Sold restant
                </span>
                <span
                  className={cn(
                    'font-mono text-base font-semibold nums',
                    client.soldRestant > 0 ? 'text-tomato' : 'text-muted-foreground',
                  )}
                >
                  {client.soldRestant > 0 ? fmtRON(client.soldRestant) : 'achitat'}
                </span>
              </div>
            </div>

            <ScrollArea className="min-h-0 flex-1">
              <Sectiune
                icon={ScrollText}
                titlu="Istoric comenzi"
                extra={`${comenzi.length} comenzi`}
              >
                {comenzi.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nicio comandă înregistrată.</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {comenzi.map((c, i) => (
                      <motion.li
                        key={c.id}
                        initial={{ opacity: 0, x: 8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.25) }}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2 shadow-paper"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-muted-foreground">{c.id}</span>
                            <StatusBadge status={c.status} />
                          </div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {fmtData(c.dataComanda)} · {fmtKg(c.totalKg)}
                          </div>
                        </div>
                        <span className="shrink-0 font-mono text-sm font-semibold nums text-amber-ink">
                          {fmtRON(c.totalRON)}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </Sectiune>

              <Separator />

              <Sectiune icon={Repeat} titlu="Comandă uzuală" extra="ce comandă de obicei">
                {uzuala.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nu a fost salvată o comandă uzuală.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {uzuala.map((u) => (
                      <span
                        key={u.produsId}
                        className="inline-flex items-center gap-1.5 rounded-full border border-amber/20 bg-amber-soft px-3 py-1 text-sm text-amber-ink"
                      >
                        <span className="font-medium">{u.produs?.nume ?? u.produsId}</span>
                        <span className="font-mono text-xs nums opacity-80">
                          {fmtKg(u.cantitateKg)}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </Sectiune>

              <Separator />

              <Sectiune icon={Tag} titlu="Preț special">
                {preturiSpeciale.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Fără prețuri negociate — se aplică prețul din catalog.
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-[0.7rem] uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-1.5 text-left font-medium">Produs</th>
                          <th className="px-3 py-1.5 text-right font-medium">Standard</th>
                          <th className="px-3 py-1.5 text-right font-medium">Preț client</th>
                          <th className="px-3 py-1.5 text-right font-medium">Reducere</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preturiSpeciale.map((p) => {
                          const dif = p.standard - p.special
                          const pct = p.standard > 0 ? Math.round((dif / p.standard) * 100) : 0
                          return (
                            <tr key={p.produsId} className="border-b border-border last:border-0">
                              <td className="px-3 py-2 font-medium">
                                {p.produs?.nume ?? p.produsId}
                              </td>
                              <td className="px-3 py-2 text-right font-mono nums text-muted-foreground line-through">
                                {fmtSuma(p.standard)}
                              </td>
                              <td className="px-3 py-2 text-right font-mono nums font-semibold text-pine">
                                {fmtSuma(p.special)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {dif > 0 ? (
                                  <Badge variant="success">−{pct}%</Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Sectiune>

              <Separator />

              <Sectiune icon={StickyNote} titlu="Notițe">
                {client.notite ? (
                  <p className="rounded-lg bg-muted/40 px-3.5 py-3 text-sm leading-relaxed text-foreground">
                    {client.notite}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Fără notițe.</p>
                )}
              </Sectiune>
            </ScrollArea>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
